import { scaleLinear } from 'd3';
import { clamp } from 'lodash';
import { useMemo } from 'react';

import { PatternDiagonalStripe } from '../../svg-patterns/patterns';
import { getOtherColor, truncateText, useChartTheme } from '../../utilities';
import axisLabelStyles from '../axis/axis-label.module.css';
import styles from './stacked-bar-chart.module.css';
import { buildGroupAxisLayout } from './stacked-bar-group-axis';
import {
  buildSegmentEntries,
  Dimensions,
  StackedBarLegend,
  type StackedBarRendererProps,
} from './stacked-bar-shared';
import {
  findNearestBarIndex,
  findSegmentAtValue,
  StackedBarSharedTooltip,
  type StackedBarTooltipState,
  useStackedBarTooltip,
} from './stacked-bar-tooltip';

const EMPTY_BAR_PATTERN_ID = 'diagonal-stripe-4';

export const VerticalStackedBar = ({
  model,
  width,
  height,
  format,
  hideLegend,
  formatLegendLabel,
  maxLegendLabelWidth,
  isTimeseries = false,
  highlightMatchingSegments = true,
}: StackedBarRendererProps) => {
  const {
    dataIndexed,
    displayLabels,
    segmentLabels,
    stackedSeries,
    emptyBars,
    colorScale,
    domainMax,
  } = model;

  const { isNewTheme } = useChartTheme();

  const legendHeight = hideLegend ? 0 : Dimensions.LegendHeight;
  const labelHeight = Dimensions.XAxisLabelHeight;
  const minWidth = dataIndexed.length * Dimensions.MinBarThickness;
  const maxWidth = dataIndexed.length * Dimensions.MaxBarThickness;
  const plotWidth = clamp(width, minWidth, maxWidth);
  const plotHeight = Math.max(0, height - legendHeight - labelHeight);
  const chartInsetLeft = Math.max(0, (width - plotWidth) / 2);

  const isPlotSizeValid = plotWidth > 0 && plotHeight > 0;
  const shouldHideLegend =
    hideLegend || legendHeight + plotHeight + labelHeight > height;

  const groupAxis = useMemo(
    () =>
      buildGroupAxisLayout({
        labels: dataIndexed.map((d) => d.label),
        isTimeseries,
        rangeLength: plotWidth,
      }),
    [dataIndexed, isTimeseries, plotWidth]
  );
  const yScale = useMemo(
    () => scaleLinear().domain([0, domainMax]).range([plotHeight, 0]),
    [domainMax, plotHeight]
  );
  const barWidth = groupAxis.barThickness;
  const focusHoveredOnly = !highlightMatchingSegments;

  // Precomputed bar x-positions — reused by hit-testing and the render.
  const barStarts = useMemo(
    () => dataIndexed.map((_, index) => groupAxis.getBarStart(index)),
    [dataIndexed, groupAxis]
  );
  const emptyIndexSet = useMemo(
    () => new Set(emptyBars.map((bar) => bar._index)),
    [emptyBars]
  );

  const xAxisEntries = useMemo(
    () =>
      groupAxis.isTime
        ? groupAxis.ticks.map((tick, index) => ({
            key: `tick-${index}`,
            center: tick.coord,
            label: tick.label,
            maxWidth: Math.max(0, groupAxis.tickSpacing - 8),
          }))
        : dataIndexed.map((d, index) => {
            const start = barStarts[index];
            return {
              key: `axis-${index}`,
              center: start === undefined ? undefined : start + barWidth / 2,
              label: displayLabels?.[index] ?? d.label,
              maxWidth: Math.max(barWidth, groupAxis.step),
            };
          }),
    [groupAxis, dataIndexed, displayLabels, barStarts, barWidth]
  );

  // Shared single tooltip: bars along x (band axis), stacked by value along y.
  // Dimming stays pure CSS (`.barContainer`/`.barContainerFocus :hover`).
  const hitTest = ([px, py]: [
    number,
    number,
  ]): StackedBarTooltipState | null => {
    const column = findNearestBarIndex(barStarts, barWidth, px);
    if (column === null) return null;
    const start = barStarts[column];
    const svgX =
      chartInsetLeft + (start === undefined ? px : start + barWidth / 2);

    if (emptyIndexSet.has(column)) {
      return {
        key: `empty:${column}`,
        svgX,
        svgY: plotHeight / 2,
        title: dataIndexed[column]?.label ?? '',
        entries: undefined,
      };
    }

    const hit = findSegmentAtValue(stackedSeries, column, yScale, py);
    if (hit === null) return null;
    return {
      key: `${column}:${hit.series.key}`,
      svgX,
      svgY: (yScale(hit.segment[0]) + yScale(hit.segment[1])) / 2,
      title: hit.series.key,
      entries: buildSegmentEntries(
        hit.series,
        hit.segment,
        format,
        focusHoveredOnly
      ),
    };
  };

  const { state, isVisible, gProps } = useStackedBarTooltip(hitTest);

  const barsContent = useMemo(
    () => (
      <>
        {stackedSeries.map((series) => (
          <g
            key={`series-${series.key}`}
            fill={colorScale(series.key) ?? getOtherColor()}
            className={styles.bar}
          >
            {series.map((segment) => {
              const x = barStarts[segment.data._index];
              if (x === undefined) return null;
              const yTop = yScale(segment[1]);
              const segmentHeight = Math.max(
                0,
                yScale(segment[0]) -
                  yScale(segment[1]) -
                  Dimensions.SegmentSpacing
              );
              if (segmentHeight === 0) return null;
              return (
                <rect
                  key={`segment-${series.key}-${segment.data._index}`}
                  x={x}
                  y={yTop}
                  fill={colorScale(series.key)}
                  width={barWidth}
                  height={segmentHeight}
                  className={styles.segment}
                  rx={isNewTheme ? 0 : 2}
                  cursor="pointer"
                />
              );
            })}
          </g>
        ))}
        {emptyBars.map((emptyBar) => {
          const x = barStarts[emptyBar._index];
          if (x === undefined) return null;
          return (
            <rect
              key={`empty-bar-${emptyBar.label}-${emptyBar._index}`}
              x={x}
              y={0}
              width={barWidth}
              height={plotHeight}
              className={styles.emptyBar}
              cursor="pointer"
              fill={`url(#${EMPTY_BAR_PATTERN_ID}) #cbd5e0`}
            />
          );
        })}
      </>
    ),
    [
      stackedSeries,
      emptyBars,
      barStarts,
      barWidth,
      yScale,
      colorScale,
      isNewTheme,
      plotHeight,
    ]
  );

  const svgHeight = plotHeight + labelHeight;
  const svgWidth = Math.max(width, plotWidth);

  return (
    <div style={{ width: '100%' }} className={styles.chartVerticalStack}>
      <div className={styles.verticalBarScroll}>
        <div className={styles.plotViewport} style={{ width: svgWidth }}>
          <svg width={svgWidth} height={svgHeight} overflow="visible">
            <defs>
              <PatternDiagonalStripe patternId={EMPTY_BAR_PATTERN_ID} />
            </defs>
            <g id="chart" transform={`translate(${chartInsetLeft}, 0)`}>
              {isPlotSizeValid && (
                <g
                  className={
                    focusHoveredOnly
                      ? styles.barContainerFocus
                      : styles.barContainer
                  }
                  {...gProps}
                >
                  {barsContent}
                </g>
              )}
            </g>
            <g
              id="x-axis"
              transform={`translate(${chartInsetLeft}, ${plotHeight})`}
            >
              {isPlotSizeValid &&
                xAxisEntries.map((entry) =>
                  entry.center === undefined ? null : (
                    <g
                      key={entry.key}
                      transform={`translate(${entry.center}, 6)`}
                    >
                      <text
                        textAnchor="middle"
                        dominantBaseline="hanging"
                        fontSize={11}
                        className={axisLabelStyles.yAxisLabel}
                        data-testid="stacked-vertical-bar-chart-x-axis-label"
                      >
                        <title>{entry.label}</title>
                        {truncateText({
                          text: entry.label,
                          maxWidth: entry.maxWidth,
                        })}
                      </text>
                    </g>
                  )
                )}
            </g>
          </svg>
          <StackedBarSharedTooltip state={state} isVisible={isVisible} />
        </div>
      </div>
      {!shouldHideLegend && isPlotSizeValid && (
        <StackedBarLegend
          className={styles.legendBelow}
          segmentLabels={segmentLabels}
          colorScale={colorScale}
          formatLegendLabel={formatLegendLabel}
          maxLegendLabelWidth={maxLegendLabelWidth}
        />
      )}
    </div>
  );
};
